import axios from "axios";
import { API_URL, getAuthHeader, getPDFAuthHeader } from ".";
import { ResponseModel } from "../Models/ResponseModel";

export type MeetingParticipant = {
  userId: number;
  name: string;
  memberId?: number | null;
  memberName?: string | null;
  role: string;
  status: string;
};

export type MeetingRequestDetails = {
  id: number;
  slotId: number;
  startUtc: string;
  endUtc: string;
  requesterUserId: number;
  recipientUserId: number;
  requesterName: string;
  recipientName: string;
  requesterMemberName?: string | null;
  recipientMemberName?: string | null;
  message?: string | null;
  status: number;
  createdUtc: string;
  updatedUtc: string;
  participants?: MeetingParticipant[];
  meetingId?: number | null;
  myRsvpStatus?: string | null;
  timeZoneId?: string | null;
};

export type ScheduleMeeting = {
  id: number;
  requestId: number;
  slotId: number;
  hostUserId: number;
  guestUserId: number;
  hostMemberId?: number | null;
  guestMemberId?: number | null;
  location?: string | null;
  notes?: string | null;
  status: number;
  createdUtc: string;
  updatedUtc: string;
  startUtc?: string | null;
  endUtc?: string | null;
  attendees?: MeetingParticipant[];
  myRsvpStatus?: string | null;
  timeZoneId?: string | null;
};

export type InviteCandidate = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  emailAddress?: string | null;
  memberName?: string | null;
};

/** Public (no auth) - fetch meeting request by token for one-click accept */
export const getMeetingRequestByToken = async (token: string) => {
  const response = await axios.get<MeetingRequestDetails>(
    `${API_URL}/api/schedule/invite/${token}`
  );
  return response.data;
};

/** Public (no auth) - accept meeting by token */
export const acceptMeetingByToken = async (token: string) => {
  const response = await axios.post(`${API_URL}/api/schedule/invite/${token}/accept`);
  return response.data;
};

/** Public (no auth) - decline meeting by token */
export const declineMeetingByToken = async (token: string) => {
  const response = await axios.post(`${API_URL}/api/schedule/invite/${token}/decline`);
  return response.data;
};

export const getMeetingRequest = async (requestId: number) => {
  const response = await axios.get<MeetingRequestDetails>(
    `${API_URL}/api/schedule/requests/${requestId}`,
    getAuthHeader()
  );
  return response.data;
};

/** All meeting requests for the current user (sent + received). */
export const getMeetingRequests = async () => {
  const response = await axios.get<MeetingRequestDetails[]>(
    `${API_URL}/api/schedule/requests`,
    getAuthHeader()
  );
  return response.data;
};

export const getMeetings = async () => {
  const response = await axios.get<ScheduleMeeting[]>(
    `${API_URL}/api/schedule/meetings`,
    getAuthHeader()
  );
  return response.data;
};

export const createSlot = async (slot: {
  startUtc: string;
  endUtc: string;
  isRecurring?: boolean;
  recurrenceRule?: string | null;
  conferenceEventId?: number | null;
}) => {
  const response = await axios.post<ResponseModel>(
    `${API_URL}/api/schedule/slots`,
    slot,
    getAuthHeader()
  );
  return response.data;
};

export const requestMeeting = async (body: {
  slotId: number;
  recipientUserIds: number[];
  message?: string | null;
}) => {
  const response = await axios.post<ResponseModel>(
    `${API_URL}/api/schedule/requests`,
    body,
    getAuthHeader()
  );
  return response.data;
};

export const createGuestMeeting = async (body: {
  startUtc: string;
  endUtc: string;
  meetingName?: string | null;
  location?: string | null;
  notes?: string | null;
  guestFirstName: string;
  guestLastName?: string | null;
  guestPhone?: string | null;
  guestEmail?: string | null;
  conferenceEventId?: number | null;
}) => {
  const response = await axios.post<ResponseModel>(
    `${API_URL}/api/schedule/guest`,
    body,
    getAuthHeader()
  );
  return response.data;
};

export const searchInviteCandidates = async (searchText: string) => {
  const response = await axios.post<InviteCandidate[]>(
    `${API_URL}/api/user/search-invite`,
    { searchText },
    getAuthHeader()
  );
  return response.data;
};

export const confirmMeeting = async (requestId: number) => {
  const response = await axios.post(
    `${API_URL}/api/schedule/confirm/${requestId}`,
    {},
    getAuthHeader()
  );
  return response.data;
};

export const cancelMeetingRequest = async (requestId: number) => {
  const response = await axios.post(
    `${API_URL}/api/schedule/cancel/${requestId}`,
    {},
    getAuthHeader()
  );
  return response.data;
};

export const rsvpMeeting = async (meetingId: number, accept: boolean) => {
  const response = await axios.post<ResponseModel>(
    `${API_URL}/api/schedule/meetings/${meetingId}/rsvp`,
    { accept },
    getAuthHeader()
  );
  return response.data;
};

export const patchMeetingDetails = async (
  meetingId: number,
  body: { location?: string | null; notes?: string | null }
) => {
  const response = await axios.patch<ResponseModel>(
    `${API_URL}/api/schedule/meetings/${meetingId}`,
    body,
    getAuthHeader()
  );
  return response.data;
};

export const deleteMeeting = async (meetingId: number, reason?: string) => {
  const response = await axios.delete<ResponseModel>(
    `${API_URL}/api/schedule/meetings/${meetingId}`,
    {
      ...getAuthHeader(),
      data: { reason: reason?.trim() || null },
    }
  );
  return response.data;
};

/** Branded printable personal schedule PDF. */
export const getMySchedulePdf = async () => {
  return axios.get(`${API_URL}/api/Reports/my-schedule`, getPDFAuthHeader());
};

export function rsvpLabel(status: string, role?: string) {
  if (role === "organizer") return "Organizer";
  if (status === "accepted") return "Accepted";
  if (status === "declined") return "Declined";
  return "Awaiting";
}

export function rsvpBadgeClass(status: string, role?: string) {
  if (role === "organizer" || status === "accepted") return "bg-green-600 text-white";
  if (status === "declined") return "bg-gray-400 text-white";
  return "bg-gray-500 text-white";
}
